import React, {useContext, useEffect, useRef, useState} from 'react'
import {FundChartData} from '../../types/FundChartData'
import {IChartMode} from '../../types/common'
import {format} from 'date-fns'
import SimpleBar from 'simplebar-react'
import {useTimeout} from 'usehooks-ts'
import SVGIcon from '../SVGIcon'
import {yyyyMMdd, getValueForDate} from '../Helpers'
import * as XLSX from 'xlsx-js-style'
import {GlobalContext} from '../ctx/GlobalContext'
import {
    firstColIndent,
    getLabelGenerated,
    percentRorFormatter,
    setColWidth,
    setFontFamily,
    tableDataCentering,
} from '../XlsxTools'
import {useTranslation} from 'react-i18next'

interface LegendTableProps {
    fundChartData: FundChartData[]
    startDateB: Date | null
    endDateB: Date | null
    toggleChartItems: (fundCode: string) => void
    chartItems: string[]
    chartMode: IChartMode
    chartItemLimit: number
    getFundColor: (key: string) => string
}

const LegendTable: React.FC<LegendTableProps> = ({
    fundChartData,
    startDateB,
    endDateB,
    toggleChartItems,
    chartItems,
    chartMode,
    chartItemLimit,
    getFundColor,
}) => {
    const {t} = useTranslation()
    const globalCtx = useContext(GlobalContext)
    const scrollTopRef = useRef(null)
    const scrollTopContentRef = useRef(null)
    const scrollBottomRef = useRef(null)
    const scrollBottomContentRef = useRef(null)
    const [scrollTopContentWidth, setScrollTopContentWidth] = useState(0)

    useTimeout(() => {
        handleResize()
    }, 500)

    const handleResize = () => {
        const {width} = scrollBottomContentRef.current.getBoundingClientRect()
        setScrollTopContentWidth(width)
    }

    const fixNameFund = (fundItem: FundChartData): string => {
        return fundItem.name ? fundItem.name?.toString().replace(/\s(.)$/i, '\u00A0$1') : ''
    }

    useEffect(() => {
        handleResize()

        window.addEventListener('resize', handleResize)
        scrollTopRef.current.recalculate()
        scrollBottomRef.current.recalculate()

        scrollTopRef.current.getScrollElement().addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
            scrollBottomRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
        })

        scrollBottomRef.current
            .getScrollElement()
            .addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
                scrollTopRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
            })

        return () => {
            window.removeEventListener('resize', handleResize)
            scrollTopRef.current.getScrollElement().removeEventListener('scroll', () => {})
            scrollBottomRef.current.getScrollElement().removeEventListener('scroll', () => {})
        }
    })

    const renderRowPrice = (fundItem: FundChartData) => {
        const startDateString = yyyyMMdd(startDateB)
        const endDateString = yyyyMMdd(endDateB)

        const startValue = getValueForDate(
            fundItem.quotation,
            startDateString,
            null
            // Use the first quotation value as the default
        )

        const endValue = getValueForDate(
            fundItem.quotation,
            endDateString,
            null // Use the last quotation value as the default
        )

        // Calculate percentage change
        let percentageChange: any = 0
        if (startValue && endValue) {
            percentageChange = ((endValue - startValue) / startValue) * 100
        }

        return (
            <div className="row-price flex">
                <div className="row-sum row-sum-comparison flex">
                    {startValue ? (
                        <span className="flex">
                            {startValue.toLocaleString('pl-PL', {
                                minimumFractionDigits: fundItem.fractionalDigits,
                                maximumFractionDigits: fundItem.fractionalDigits,
                            })}{' '}
                            {fundItem.currency}
                        </span>
                    ) : (
                        <span className="flex">&ndash;</span>
                    )}
                    {Date.parse(fundItem.quotation[0].date) > Date.parse(startDateString as string) && (
                        <div className="row-ju flex">
                            <span>
                                {t('label.chart.legend.fund-started')}{' '}
                                {format(fundItem.quotation[0].date, 'dd.MM.yyyy')}
                            </span>
                        </div>
                    )}
                </div>
                <div className="row-sum row-sum-comparison flex">
                    {endValue ? (
                        <span className="flex">
                            {endValue.toLocaleString('pl-PL', {
                                minimumFractionDigits: fundItem.fractionalDigits,
                                maximumFractionDigits: fundItem.fractionalDigits,
                            })}{' '}
                            {fundItem.currency}
                        </span>
                    ) : (
                        <span className="flex">&ndash;</span>
                    )}
                    {Date.parse(fundItem.quotation[fundItem.quotation.length - 1].date) <
                        Date.parse(endDateString as string) && (
                        <div className="row-ju flex">
                            <span className="flex">
                                {t('label.chart.legend.fund-last-quotation')}{' '}
                                {format(fundItem.quotation[fundItem.quotation.length - 1].date, 'dd.MM.yyyy')}
                            </span>
                        </div>
                    )}
                </div>
                <div className="elements-percent elements-percent-comparison flex">
                    {startValue && endValue ? (
                        <>
                            <div className="row-percent row-ju flex">
                                <div className="flex percent">
                                    {percentageChange.toLocaleString('pl-PL', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                    %
                                </div>
                            </div>
                            <div className="arrow">
                                <div
                                    className={`arrow-parameters ${percentageChange >= 0 ? 'arrow-up' : 'arrow-down'}`}></div>
                            </div>
                        </>
                    ) : (
                        <div className="row-percent row-ju flex">
                            <div className="flex percent">&ndash;</div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const downloadCompareQuot = () => {
        //Setting data
        const xlsxStruct: Array<any> = []
        fundChartData.map((fi: FundChartData) => {
            const valueFractionDigits = {
                minimumFractionDigits: fi.fractionalDigits,
                maximumFractionDigits: fi.fractionalDigits,
            }

            const startYmd = yyyyMMdd(startDateB)
            const endYmd = yyyyMMdd(endDateB)
            const quot = fi.quotation
            const sv = getValueForDate(quot, startYmd, null)
            const ev = getValueForDate(quot, endYmd, null)

            let percentageChange: null | number = null
            if (sv && ev) percentageChange = (ev - sv) / sv //* 100

            const xlsxItem = {
                [t('label.xlsx.common.fund')]: fi.name,
                [t('label.xlsx.common.value') + startYmd]: sv ? sv.toLocaleString('pl-PL', valueFractionDigits) : '-',
                [t('label.xlsx.common.currency') + startYmd]: fi.currency,
                [t('label.xlsx.common.value') + endYmd]: ev ? ev.toLocaleString('pl-PL', valueFractionDigits) : '-',
                [t('label.xlsx.common.currency') + endYmd]: fi.currency,
                [t('label.xlsx.common.value')]: percentageChange ? Number(percentageChange.toFixed(4)) : '-',
            }

            xlsxStruct.push(xlsxItem)
        })
        //Style XLSX
        const fileDate = new Date()
        let headerTitle: string
        let filename: string
        let tabTitle: string | undefined

        if (chartMode === 'profile') {
            const fName = fundChartData[0].name
            headerTitle = fName + ' - ' + t('label.xlsx.legend.title')
            filename = 'Allianz.pl - ' + fName + ' - ' + fileDate.getTime() + '.xlsx'
            tabTitle = fName.slice(0, 31)
        } else {
            headerTitle = t('label.xlsx.list.filename.' + globalCtx.layout) + ' - ' + t('label.xlsx.legend.title')
            filename =
                'Allianz.pl - ' +
                t('label.xlsx.list.filename.' + globalCtx.layout) +
                ' - ' +
                fileDate.getTime() +
                '.xlsx'
            tabTitle = t('label.xlsx.list.filename.' + globalCtx.layout)
        }

        const maxColNum = Object.keys(xlsxStruct[0]).length
        const headerRowForFundData: number = 5
        const ws = XLSX.utils.json_to_sheet([])
        ws['!merges'] = []

        const additionalRows = [{}, {A: headerTitle}, {A: getLabelGenerated(t, fileDate)}, {}]

        const datesHeaderStyle = {
            font: {sz: 12},
            alignment: {horizontal: 'center', vertical: 'center'},
        }

        // setting date headers and removing date from json key
        ws['B4'] = {}
        ws['B4'].v = yyyyMMdd(startDateB)
        ws['B4'].s = datesHeaderStyle
        ws['D4'] = {}
        ws['D4'].v = yyyyMMdd(endDateB)
        ws['D4'].s = datesHeaderStyle
        ws['F4'] = {}
        ws['F4'].v = t('label.chart.tab.rate-of-return')
        ws['F4'].s = datesHeaderStyle
        ws['!merges']?.push(XLSX.utils.decode_range('B4:C4'))
        ws['!merges']?.push(XLSX.utils.decode_range('D4:E4'))

        ws['!rows'] = [{hpx: 25}, {hpx: 20}, {hpx: 20}, {hpx: 30}, {hpx: 30}]
        XLSX.utils.sheet_add_json(ws, additionalRows, {skipHeader: true, origin: 0})
        XLSX.utils.sheet_add_json(ws, xlsxStruct, {origin: additionalRows.length})

        ws['B5'].v = ws['B5'].v?.slice(0, -10)
        ws['C5'].v = ws['C5'].v?.slice(0, -10)
        ws['D5'].v = ws['D5'].v?.slice(0, -10)
        ws['E5'].v = ws['E5'].v?.slice(0, -10)

        ws['A2'].s = {
            font: {sz: 18, color: {rgb: '006192'}},
        }

        ws['A3'].s = {
            font: {sz: 11, color: {rgb: '767676'}},
        }

        ws['!cols'] = []
        for (let col = 0; col < maxColNum; col++) {
            const cellName = String.fromCharCode(col + 65) + '' + headerRowForFundData
            ws[cellName].s = {
                font: {sz: 10, bold: true},
                alignment: {horizontal: 'center', vertical: 'center'},
                border: {
                    top: col != 0 ? {style: 'thin', color: {rgb: '000000'}} : undefined,
                    bottom: {style: 'thin', color: {rgb: '000000'}},
                },
            }
        }

        setColWidth(ws, maxColNum, 20, [50])
        tableDataCentering(ws, headerRowForFundData, maxColNum)
        firstColIndent(ws)

        const colWithRor = 5
        percentRorFormatter(ws, colWithRor, colWithRor, headerRowForFundData)

        // white background and hidden borders for header
        ws['!merges']?.push(XLSX.utils.decode_range('A2:E2'))
        ws['!merges']?.push(XLSX.utils.decode_range('A3:E3'))

        setFontFamily(ws, 'Arial')
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, tabTitle)
        XLSX.writeFile(wb, filename)
    }

    return (
        <>
            <div id={'tableLegend'} className="fund-table fund-table-legend" style={{padding: '32px 0 0 0'}}>
                <div className="move-scrollbar"> {t('label.scroll.more')}</div>
                <SimpleBar
                    autoHide={false}
                    forceVisible={false}
                    scrollbarMinSize={0}
                    scrollbarMaxSize={1118}
                    ref={scrollTopRef}>
                    <div
                        className="scrollbar-top"
                        style={{width: scrollTopContentWidth}}
                        ref={scrollTopContentRef}></div>
                </SimpleBar>

                <SimpleBar
                    autoHide={false}
                    forceVisible={false}
                    scrollbarMinSize={0}
                    scrollbarMaxSize={1118}
                    ref={scrollBottomRef}>
                    <div className="fund-table-all">
                        <div
                            className="fund-table-row flex"
                            style={{
                                borderBottom: '2px solid var(--hexcolor1)',
                            }}>
                            <div className="fund-wrapper" ref={scrollBottomContentRef}>
                                <div className="row-wrapper row-wrapper-compare">
                                    {chartMode === 'compare' ? (
                                        <div className="column-tab-comparison flex" style={{alignItems: 'center'}}>
                                            {t('label.compare.visible.fund')} ({chartItems.length}/{chartItemLimit})
                                        </div>
                                    ) : (
                                        <div className="column-tab-comparison flex"></div>
                                    )}
                                    <div
                                        className="row-price row-price-comparison  flex"
                                        style={{alignItems: 'center'}}>
                                        <div className="row-sum row-sum-comparison flex font-600">
                                            {t('label.compare.unit.value')}
                                            <div className="flex">{startDateB && format(startDateB, 'dd.MM.yyyy')}</div>
                                        </div>
                                        <div className="row-sum row-sum-comparison flex font-600">
                                            {t('label.compare.unit.value')}
                                            <div className="flex">{endDateB && format(endDateB, 'dd.MM.yyyy')}</div>
                                        </div>
                                        <div className="elements-percent elements-percent-comparison flex">
                                            {t('label.chart.tab.rate-of-return')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {fundChartData.map((fundItem) => (
                            <div className="fund-table-row flex" key={'upperLegend_' + fundItem.fundCode}>
                                <div className="fund-wrapper">
                                    <div className="row-wrapper row-wrapper-ofe row-wrapper-compare std-hover ">
                                        <div className="column-tab-comparison flex">
                                            {chartMode !== 'profile' && globalCtx.layout !== 'ofe' ? (
                                                <div className="switch">
                                                    <label className="switch">
                                                        <input
                                                            type="checkbox"
                                                            onChange={() => toggleChartItems(fundItem.fundCode)}
                                                            checked={chartItems.includes(fundItem.fundCode)}
                                                            disabled={
                                                                chartItems.length == chartItemLimit &&
                                                                !chartItems.includes(fundItem.fundCode)
                                                            }
                                                        />
                                                        <span className="slider round "></span>
                                                    </label>
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                            <div className="checkbox">
                                                <div className="legend legend-table flex" style={{paddingTop: '2px'}}>
                                                    <span
                                                        className="chart-dot"
                                                        style={{
                                                            background: getFundColor(fundItem.fundCode),
                                                        }}></span>
                                                </div>
                                            </div>
                                            <div className="row-fund row-fund-compare flex">
                                                <div className="row-name flex">{fixNameFund(fundItem)}</div>
                                            </div>
                                        </div>

                                        {renderRowPrice(fundItem)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </SimpleBar>
                <div className="download download-compared flex">
                    <button
                        className="download-btn flex"
                        style={{alignItems: 'flex-end'}}
                        onClick={downloadCompareQuot}>
                        <span className="download-icon" style={{gap: '8px'}}>
                            <span className="download-text download-text-xlsx">{t('label.btn.xlsx.download')}</span>
                            <SVGIcon
                                id={'download-pdf'}
                                className={'light-blue'}
                                width={24}
                                height={24}
                                fill={'#006192'}
                            />
                        </span>
                    </button>
                </div>
            </div>
        </>
    )
}

export default LegendTable
